/**
 * The "My Dashboards" flag.
 *
 * A dashboard marked `showInMyDashboards` is surfaced in the product's My
 * Dashboards section. Nothing is opted in implicitly — the flag is off unless an
 * admin ticks it on create or edit.
 *
 * The wire key is `showInMyDashboards`, camelCase, like the rest of
 * `POST /agents/dashboards` and `PATCH /agents/dashboards/:id`. Reads accept
 * either casing and the string/number booleans the API has used for other flags,
 * the same tolerance `readStalenessFields` applies to the staleness fields.
 */

/** Dashboards stay out of My Dashboards until an admin opts them in. */
export const SHOW_IN_MY_DASHBOARDS_DEFAULT = false;

/** Request/response key, in the casing the create/update body uses. */
export const SHOW_IN_MY_DASHBOARDS_FIELD = "showInMyDashboards";

const SHOW_IN_MY_DASHBOARDS_READ_KEYS = [
  "showInMyDashboards",
  "show_in_my_dashboards",
];

/** True for `true` / `"true"` / `1` / `"1"`; false for anything else. */
export function coerceShowInMyDashboards(value) {
  return value === true || value === "true" || value === 1 || value === "1";
}

/**
 * The flag from an API dashboard, whatever casing it arrives in. Falls back to
 * the default when the field is missing, so rows written before the backend had
 * the column read as "not in My Dashboards" rather than as undefined.
 */
export function readShowInMyDashboards(source) {
  if (!source || typeof source !== "object") return SHOW_IN_MY_DASHBOARDS_DEFAULT;
  for (const key of SHOW_IN_MY_DASHBOARDS_READ_KEYS) {
    const value = source[key];
    if (value !== undefined && value !== null) return coerceShowInMyDashboards(value);
  }
  return SHOW_IN_MY_DASHBOARDS_DEFAULT;
}

/**
 * The flag's part of a create/update body. Always sent, as a real boolean, in
 * both modes — so unticking it on edit persists instead of reading as
 * "leave as-is".
 */
export function showInMyDashboardsPayload(value) {
  return { [SHOW_IN_MY_DASHBOARDS_FIELD]: coerceShowInMyDashboards(value) };
}
